import * as React from "react";
import { ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { EnvContext } from "@/contexts/appContexts";
import { BackgroundPolicy } from "@/services/bedrock/dtos/BackgroundAsset";
import { AspectRatio, ContextType, DesignBackground } from "@/services/bedrock/dtos/DesignPackage";
import {
  BackgroundPickerPopover,
  BG_UPLOAD_FAILED,
  BG_UPLOAD_TOO_LARGE,
  uploadPlayerBackground,
} from "./BackgroundPickerPopover";
import type { BackgroundChoice } from "./ShareFlowProvider";
import { StyleCell } from "./StyleBar";

export interface BackgroundCellProps {
  backgrounds: DesignBackground[];
  policy: BackgroundPolicy;
  currentAssetId: number | null;
  currentUrl: string | null;
  isCarousel: boolean;
  disabled: boolean;
  onSelect: (choice: BackgroundChoice) => void;
  /** Whose folder an upload goes to. */
  playerId: number | null;
  ratio: AspectRatio;
  contextType: ContextType;
}

/** Whether there's a choice to offer; a suggested-only design with one background has none. */
export const hasBackgroundChoice = (backgrounds: DesignBackground[], policy: BackgroundPolicy) =>
  !(backgrounds.length < 2 && policy === "suggested");

export function backgroundLabel(
  backgrounds: DesignBackground[],
  currentAssetId: number | null,
  currentUrl: string | null
): string {
  if (currentUrl) return "Your photo";
  const defaultAssetId = backgrounds.find(b => b.isDefault)?.assetId ?? backgrounds[0]?.assetId ?? null;
  if (currentAssetId == null || currentAssetId === defaultAssetId) return "Default";
  const attached = backgrounds.findIndex(b => b.assetId === currentAssetId);
  return attached >= 0 ? `Option ${attached + 1}` : "From the library";
}

export const BackgroundCell: React.FC<BackgroundCellProps> = ({
  backgrounds,
  policy,
  currentAssetId,
  currentUrl,
  isCarousel,
  disabled,
  onSelect,
  playerId,
  ratio,
  contextType,
}) => {
  const envValues = React.useContext(EnvContext);
  const [open, setOpen] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);
  // A library pick isn't among the design's backgrounds, so its picture is remembered when picked.
  const [pickedUrls, setPickedUrls] = React.useState<Record<number, string>>({});

  const choose = (choice: BackgroundChoice, previewUrl?: string) => {
    setOpen(false);
    if (choice && "assetId" in choice && previewUrl) setPickedUrls(urls => ({ ...urls, [choice.assetId]: previewUrl }));
    onSelect(choice);
  };

  const onUploadFile = async (file: File) => {
    if (playerId == null) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const url = await uploadPlayerBackground(file, playerId, setUploadProgress, envValues);
      choose({ url });
    } catch (error) {
      toast.error(
        error instanceof Error && error.message === BG_UPLOAD_TOO_LARGE ? BG_UPLOAD_TOO_LARGE : BG_UPLOAD_FAILED
      );
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const defaultBackground = backgrounds.find(b => b.isDefault) ?? backgrounds[0];
  const previewUrl =
    currentUrl ??
    (currentAssetId != null
      ? backgrounds.find(b => b.assetId === currentAssetId)?.url ?? pickedUrls[currentAssetId]
      : defaultBackground?.url);

  return (
    <StyleCell
      label="Background"
      value={backgroundLabel(backgrounds, currentAssetId, currentUrl)}
      preview={
        previewUrl ? (
          <img src={previewUrl} alt="" className="size-full object-cover" />
        ) : (
          <ImageIcon className="size-4 text-slate-400" />
        )
      }
      disabled={disabled}
      open={open}
      onOpenChange={setOpen}
      contentClassName="w-[min(92vw,560px)] p-3"
      data-testid="background-cell"
    >
      <BackgroundPickerPopover
        backgrounds={backgrounds}
        policy={policy}
        ratio={ratio}
        contextType={contextType}
        currentAssetId={currentAssetId}
        currentUrl={currentUrl}
        isCarousel={isCarousel}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onSelect={choose}
        onUploadFile={onUploadFile}
      />
    </StyleCell>
  );
};
